"use client";

import { Box, Button, Text, Stack, Group } from "@mantine/core";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { IconArrowRight, IconCamera } from "@tabler/icons-react";
import Image from "next/image";
import { useRouter } from "@/i18n/navigation";
import { useRef } from "react";
import { useTranslations } from "next-intl";
import { trackToolPhotoUpload } from "@/lib/analytics";
import SampleReportButton from "./SampleReportButton";

interface QuizPhotoUploadProps {
  quizName?: string;
  /** The free result just shown (e.g. "True Winter") — personalizes the headline. */
  resultLabel?: string;
  headline?: string;
  subtext?: string;
  /** Opt in to the paid-report sample without changing other quiz upload offers. */
  sampleReportSource?: string;
}

const resizeImage = (file: File, maxWidth: number, maxHeight: number): Promise<string> => {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => {
      let { width, height } = img;
      const scale = Math.min(maxWidth / width, maxHeight / height, 1);
      width = Math.round(width * scale);
      height = Math.round(height * scale);

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (ctx) ctx.drawImage(img, 0, 0, width, height);

      const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
      URL.revokeObjectURL(img.src);
      resolve(dataUrl);
    };
    img.src = URL.createObjectURL(file);
  });
};

// The selfie → drapes strip from the homepage hero: show the outcome, not a cloud icon.
const STRIP = [
  { src: "/mainLanding/hero/drape-azure.webp", alt: "Draped in azure" },
  { src: "/mainLanding/hero/drape-magenta.webp", alt: "Draped in magenta" },
  { src: "/mainLanding/hero/drape-burgundy.webp", alt: "Draped in burgundy" },
];

export default function QuizPhotoUpload({ quizName, resultLabel, headline, subtext, sampleReportSource }: QuizPhotoUploadProps) {
  const router = useRouter();
  const t = useTranslations("quizUpload");
  const openRef = useRef<() => void>(null);

  const handlePhotoDrop = async (files: File[]) => {
    if (files.length > 0) {
      if (quizName) {
        trackToolPhotoUpload(quizName);
      }
      const dataUrl = await resizeImage(files[0], 1200, 1200);
      sessionStorage.setItem("colorAnalysisImage", dataUrl);
      router.push("/ai-color-analysis/result");
    }
  };

  const heading = headline ?? (resultLabel ? t("headlineResult", { result: resultLabel }) : t("headlineGeneric"));
  const sub = subtext ?? t("subGeneric");

  return (
    <Box w="100%">
    <Dropzone
      onDrop={handlePhotoDrop}
      maxSize={10 * 1024 ** 2}
      accept={IMAGE_MIME_TYPE}
      multiple={false}
      openRef={openRef}
      activateOnClick={false}
      radius={16}
      p={0}
      w="100%"
      styles={{
        root: {
          backgroundColor: "#ffffff",
          border: "1px solid #e2e8f0",
          boxShadow: "0 10px 30px rgba(22,24,29,0.06)",
          cursor: "default",
          "&[dataAccept]": { borderColor: "#22c55e" },
          "&[dataReject]": { borderColor: "#ef4444" },
        },
      }}
    >
      <Stack gap="sm" p="lg" ta="center" align="center">
        <Text fz={11} fw={600} tt="uppercase" style={{ letterSpacing: "0.14em", color: "#b3265c" }}>
          {t("step")}
        </Text>
        <Text fw={650} fz="lg" lh={1.25} style={{ color: "#16181d" }}>
          {heading}
        </Text>
        <Text fz="sm" c="dimmed" maw={420}>
          {sub}
        </Text>

        <Group gap={6} justify="center" wrap="nowrap" my={4}>
          <Image
            src="/mainLanding/hero/selfie.webp"
            alt="A phone selfie"
            width={52}
            height={66}
            style={{ borderRadius: 8, objectFit: "cover" }}
          />
          <IconArrowRight size={16} color="#8b8b98" style={{ flexShrink: 0 }} />
          {STRIP.map((s) => (
            <Image
              key={s.src}
              src={s.src}
              alt={s.alt}
              width={52}
              height={66}
              style={{ borderRadius: 8, objectFit: "cover" }}
            />
          ))}
        </Group>
        <Text fz={10} fw={500} tt="uppercase" style={{ letterSpacing: "0.12em", color: "#8b8b98" }}>
          {t("stripCaption")}
        </Text>

        <Button
          onClick={() => openRef.current?.()}
          fullWidth
          size="md"
          radius="xl"
          leftSection={<IconCamera size={18} />}
          styles={{ root: { background: "#16181d", "&:hover": { background: "#2a2d35" } } }}
        >
          {t("cta")}
        </Button>

        <Text fz="xs" style={{ color: "#94a3b8" }}>
          {t("dropHint")}
        </Text>
        <Text fz="xs" style={{ color: "#94a3b8" }}>
          {t("trust")}
        </Text>
      </Stack>
    </Dropzone>
    {sampleReportSource && <Box ta="center">
      <SampleReportButton
        source={sampleReportSource}
        continueLabel={t("cta")}
        onContinue={() => openRef.current?.()}
      />
    </Box>}
    </Box>
  );
}
